/**
 * Single-click placement tools: pad, via, junction, text and arc.
 *
 * Defaults come from the document's `DefaultPad::` / `DefaultVia::` /
 * `DefaultText::` records rather than being hard-coded, so a board imported
 * from CIRCAD keeps drawing with the settings it was authored with.
 */

import type { ArcEntity, PadEntity, TextEntity } from '@sircad/core/model.ts';
import { LayerType, newId, padIsThrough } from '@sircad/core/model.ts';
import { PAD_MASTER_LAYER } from '@sircad/core/geom/board.ts';
import type { Point } from '../render/viewport.ts';
import { snap } from '../render/viewport.ts';
import type { Tool, ToolContext } from './tool.ts';

export class PadTool implements Tool {
  readonly id = 'pad';
  readonly label = 'Pad';
  readonly key = 'p';
  readonly hint = 'Click to place a pad using the document default';
  readonly cursor = 'crosshair';

  // Through-hole pads belong on the pad master so they reach every copper
  // layer, which is where the whole stock through-hole library puts them.
  readonly layerTypes = {
    sch: [LayerType.Schematic, LayerType.Symbols] as const,
    pcb: [LayerType.Copper] as const,
  };

  onPointerDown(p: Point, ctx: ToolContext): void {
    const d = ctx.doc();
    const def = d.defaults.pad;
    const g = ctx.grid;
    const type = def?.type ?? (d.kind === 'sch' ? 'T0' : 'T1');

    // On a board, a through-hole pad belongs on the pad master so it reaches
    // every copper layer — that is where the whole stock through-hole library
    // puts its pads. Surface-mount pads go on the selected copper layer, and
    // the original refuses them on the pad master.
    const layer = d.kind === 'pcb' && padIsThrough(type)
      ? PAD_MASTER_LAYER
      : ctx.activeLayer;

    const pad: PadEntity = {
      kind: 'pad',
      id: newId(),
      layer,
      type,
      x: snap(p.x, g),
      y: snap(p.y, g),
      sizeX: def?.size ?? 0.06,
      sizeY: def?.size ?? 0.06,
    };
    if (def?.hole && padIsThrough(type)) pad.hole = def.hole;

    ctx.beginEdit('Place pad');
    d.entities.push(pad);
    ctx.commit();
    ctx.setMessage(
      `${type} pad on ${d.layers[layer]?.name ?? layer}`
      + (layer === PAD_MASTER_LAYER ? ' — reaches every copper layer' : ''),
    );
  }
}

export class ViaTool implements Tool {
  readonly id = 'via';
  readonly label = 'Via';
  readonly key = 'v';
  readonly hint = 'Click to place a via';
  readonly cursor = 'crosshair';
  readonly appliesTo = ['pcb'] as const satisfies readonly ('sch' | 'pcb')[];
  readonly layerTypes = { pcb: [LayerType.Copper] as const };

  onPointerDown(p: Point, ctx: ToolContext): void {
    const d = ctx.doc();
    const def = d.defaults.via;
    const g = ctx.grid;

    const via: PadEntity = {
      kind: 'pad',
      id: newId(),
      layer: ctx.activeLayer,
      type: def?.type ?? 'T3',
      x: snap(p.x, g),
      y: snap(p.y, g),
      sizeX: def?.size ?? 0.05,
      sizeY: def?.size ?? 0.05,
      hole: def?.hole ?? 0.028,
    };

    ctx.beginEdit('Place via');
    d.entities.push(via);
    ctx.commit();
  }
}

/**
 * Junction dot. In a schematic a crossing without one is not a connection, so
 * this is how you assert that two wires actually join.
 */
export class JunctionTool implements Tool {
  readonly id = 'junction';
  readonly label = 'Junction';
  readonly key = 'j';
  readonly hint = 'Click where two wires should connect';
  readonly cursor = 'crosshair';
  readonly appliesTo = ['sch'] as const satisfies readonly ('sch' | 'pcb')[];
  readonly layerTypes = { sch: [LayerType.Schematic] as const };

  onPointerDown(p: Point, ctx: ToolContext): void {
    const g = ctx.grid;
    const dot: PadEntity = {
      kind: 'pad',
      id: newId(),
      layer: ctx.activeLayer,
      type: 'S3',
      x: snap(p.x, g),
      y: snap(p.y, g),
      // Forty mil, as the original states outright: Place Signal Dot creates
      // "a 40-mil diameter surface-via on the signals layer". It was thirty.
      sizeX: 0.04,
      sizeY: 0.04,
    };

    ctx.beginEdit('Place junction');
    ctx.doc().entities.push(dot);
    ctx.commit();
  }
}

export class TextTool implements Tool {
  readonly id = 'text';
  readonly label = 'Text';
  readonly key = 'x';
  readonly hint = 'Click to place text';
  readonly cursor = 'text';

  // Board text is silkscreen; schematic text is a symbol annotation.
  readonly layerTypes = {
    sch: [LayerType.Symbols] as const,
    pcb: [LayerType.Silk] as const,
  };

  onPointerDown(p: Point, ctx: ToolContext): void {
    const d = ctx.doc();
    const value = window.prompt('Text:');
    if (value === null || value === '') return;

    const g = ctx.grid;
    const text: TextEntity = {
      kind: 'text',
      id: newId(),
      layer: ctx.activeLayer,
      x: snap(p.x, g),
      y: snap(p.y, g),
      height: d.defaults.text?.height ?? 0.06,
      anchor: 'BL',
      text: value,
    };

    ctx.beginEdit('Place text');
    d.entities.push(text);
    ctx.commit();
  }
}

/**
 * Three-point arc: click start, click end, then move to set the bulge and
 * click to confirm. Matches the original's arc entry.
 */
export class ArcTool implements Tool {
  readonly id = 'arc';
  readonly label = 'Arc';
  readonly key = 'a';
  readonly hint = 'Click start, click end, then move to set the curve and click';
  readonly cursor = 'crosshair';

  // Curves are usually board outline or silk, not copper.
  readonly layerTypes = {
    sch: [LayerType.Symbols] as const,
    pcb: [LayerType.Drawing, LayerType.Silk] as const,
  };

  private start: Point | null = null;
  private end: Point | null = null;

  onPointerDown(p: Point, ctx: ToolContext): void {
    const g = ctx.grid;
    const at = { x: snap(p.x, g), y: snap(p.y, g) };

    if (!this.start) { this.start = at; return; }
    if (!this.end) { this.end = at; return; }

    const arc = this.build(p, ctx);
    if (arc) {
      ctx.beginEdit('Draw arc');
      ctx.doc().entities.push(arc);
      ctx.commit();
    }
    this.reset(ctx);
  }

  onPointerMove(p: Point, ctx: ToolContext): void {
    if (!this.start) return;
    ctx.preview.length = 0;

    if (!this.end) {
      const g = ctx.grid;
      ctx.preview.push({
        kind: 'line', id: -1, layer: ctx.activeLayer,
        width: ctx.doc().defaults.line?.width ?? 0.01,
        x1: this.start.x, y1: this.start.y,
        x2: snap(p.x, g), y2: snap(p.y, g),
      });
    } else {
      const arc = this.build(p, ctx);
      if (arc) ctx.preview.push(arc);
    }
    ctx.redraw();
  }

  cancel(ctx: ToolContext): void {
    this.reset(ctx);
    ctx.redraw();
  }

  onDeactivate(ctx: ToolContext): void {
    this.reset(ctx);
  }

  /** Circle through the two fixed points and the cursor. */
  private build(p: Point, ctx: ToolContext): ArcEntity | null {
    const a = this.start;
    const b = this.end;
    if (!a || !b) return null;

    // Perpendicular distance from the cursor to the chord gives the sagitta,
    // from which the radius and sweep follow.
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const chord = Math.hypot(dx, dy);
    if (chord < 1e-9) return null;

    const side = ((p.x - a.x) * dy - (p.y - a.y) * dx) / chord;
    if (Math.abs(side) < 1e-9) return null;

    const h = -side;
    const radius = (chord * chord / 4 + h * h) / (2 * Math.abs(h));

    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const nx = -dy / chord;
    const ny = dx / chord;
    const d = radius - Math.abs(h);
    const sign = h > 0 ? 1 : -1;
    const cx = mx - nx * d * sign;
    const cy = my - ny * d * sign;

    let a1 = (Math.atan2(a.y - cy, a.x - cx) * 180) / Math.PI;
    let a2 = (Math.atan2(b.y - cy, b.x - cx) * 180) / Math.PI;
    let sweep = a2 - a1;
    while (sweep <= -180) sweep += 360;
    while (sweep > 180) sweep -= 360;
    if (h > 0 && sweep < 0) sweep += 360;
    if (h < 0 && sweep > 0) sweep -= 360;
    void a2;

    return {
      kind: 'arc',
      id: -1,
      layer: ctx.activeLayer,
      width: ctx.doc().defaults.line?.width ?? 0.01,
      cx, cy, radius,
      angle: a1,
      sweep,
    };
  }

  private reset(ctx: ToolContext): void {
    this.start = null;
    this.end = null;
    ctx.preview.length = 0;
  }
}
